"use client";

import { useRouter } from "next/navigation";
import { useState, useTransition } from "react";
import { checkNameAvailable, deleteOwnAccount, updateProfile } from "@/actions/profile";
import type { GroupRole } from "@/lib/store";
import {
  RoleGroupPicker,
  isRoleGroupReady,
  sortGroupNos,
  type RoleGroupValue,
} from "./role-group-picker";
import { PasswordChangeForm } from "./password-change-form";
import { Sheet } from "./sheet";
import { Button, ErrorText, Field, Input } from "./ui";

type Props = {
  displayName: string;
  groupRole: GroupRole;
  groupNos: number[];
};

export function ProfileEdit({ displayName, groupRole, groupNos }: Props) {
  const router = useRouter();
  const [pending, start] = useTransition();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"profile" | "password" | "delete">("profile");
  const [name, setName] = useState(displayName);
  const [value, setValue] = useState<RoleGroupValue>({
    role: groupRole,
    groupNos,
  });
  const [error, setError] = useState("");
  const [deletionPassword, setDeletionPassword] = useState("");
  const [nameCheck, setNameCheck] = useState<null | { ok: boolean; text: string }>(null);

  /** 열 때마다 저장된 값으로 되돌린다 — 고치다 만 상태가 남지 않게. */
  function openSheet() {
    setName(displayName);
    setValue({ role: groupRole, groupNos });
    setError("");
    setDeletionPassword("");
    setNameCheck(null);
    setMode("profile");
    setOpen(true);
  }

  function checkName() {
    setError("");
    start(async () => {
      const res = await checkNameAvailable(name);
      if (!res.ok) {
        setNameCheck({ ok: false, text: res.error });
        return;
      }
      setNameCheck(
        res.available
          ? { ok: true, text: "쓸 수 있는 닉네임이에요." }
          : { ok: false, text: "이미 등록된 이름이에요." },
      );
    });
  }

  function save() {
    if (value.role === null) return;
    setError("");
    start(async () => {
      const res = await updateProfile({
        displayName: name,
        groupRole: value.role,
        groupNos: sortGroupNos(value.groupNos),
      });
      if (!res.ok) {
        setError(res.error);
        return;
      }
      setOpen(false);
      router.refresh();
    });
  }

  function removeAccount() {
    setError("");
    start(async () => {
      const result = await deleteOwnAccount({ currentPassword: deletionPassword });
      if (!result.ok) {
        setError(result.error);
        return;
      }
      router.replace("/join");
      router.refresh();
    });
  }

  return (
    <>
      <Button type="button" variant="outline" size="sm" onClick={openSheet}>
        수정
      </Button>

      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title={mode === "password" ? "비밀번호 변경" : mode === "delete" ? "회원 탈퇴" : "내 정보 수정"}
        dismissible={!pending}
      >
        {mode === "delete" ? (
          <form
            className="mt-5"
            onSubmit={(event) => {
              event.preventDefault();
              removeAccount();
            }}
          >
            <p className="text-[17px] font-extrabold">정말 탈퇴하시겠습니까?</p>
            <p className="mt-2 text-[14px] leading-relaxed text-muted">
              이 행동은 되돌릴 수 없습니다. 로그인 정보, 등록된 시간표와 원본 이미지, 개인 응답 및
              댓글이 모두 삭제됩니다.
            </p>

            <div className="mt-5">
              <Field label="지금 비밀번호">
                <Input
                  type="password"
                  autoComplete="current-password"
                  value={deletionPassword}
                  onChange={(event) => setDeletionPassword(event.target.value)}
                  minLength={4}
                  maxLength={64}
                  placeholder="본인 확인을 위해 입력해 주세요"
                />
              </Field>
            </div>

            <div className="mt-4">
              <ErrorText>{error}</ErrorText>
            </div>

            <div className="mt-6 space-y-2">
              <Button
                type="submit"
                full
                disabled={pending || deletionPassword.length < 4}
                className="!bg-danger !text-white"
              >
                {pending ? "탈퇴하는 중…" : "비밀번호 확인 후 탈퇴"}
              </Button>
              <Button
                type="button"
                full
                variant="ghost"
                disabled={pending}
                onClick={() => {
                  setError("");
                  setDeletionPassword("");
                  setMode("profile");
                }}
              >
                돌아가기
              </Button>
            </div>
          </form>
        ) : mode === "password" ? (
          <PasswordChangeForm onBack={() => setMode("profile")} />
        ) : (
          <>
            <div className="mt-5">
              <Field
                label="닉네임"
                hint="모임에서 다른 사람에게 보여줄 이름이에요."
              >
                <div className="flex gap-2">
                  <Input
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      // 이름이 바뀌면 앞서 본 결과는 더 이상 이 이름의 결과가 아니다
                      setNameCheck(null);
                    }}
                    maxLength={9}
                    placeholder="닉네임"
                  />
                  <Button
                    variant="secondary"
                    className="shrink-0 px-4 text-[15px]"
                    disabled={pending || name.trim().length === 0}
                    onClick={checkName}
                  >
                    중복 확인
                  </Button>
                </div>
              </Field>

              {nameCheck ? (
                <p
                  className={`mt-2 text-[13px] font-bold ${
                    nameCheck.ok ? "text-accent" : "text-danger"
                  }`}
                >
                  {nameCheck.text}
                </p>
              ) : null}

              <button
                type="button"
                onClick={() => setMode("password")}
                className="mt-4 text-[15px] font-bold text-accent"
              >
                비밀번호 변경하기 →
              </button>
            </div>

            <div className="mt-6 border-t border-line/70 pt-5">
              <RoleGroupPicker value={value} onChange={setValue} />
            </div>

            <ErrorText>{error}</ErrorText>

            <div className="mt-6 space-y-2">
              <Button
                full
                disabled={pending || name.trim().length === 0 || !isRoleGroupReady(value)}
                onClick={save}
              >
                저장하기
              </Button>
              <Button full variant="ghost" disabled={pending} onClick={() => setOpen(false)}>
                취소
              </Button>
            </div>

            <div className="mt-8 border-t border-line/70 pt-5 text-center">
              <button
                type="button"
                disabled={pending}
                onClick={() => {
                  setError("");
                  setDeletionPassword("");
                  setMode("delete");
                }}
                className="text-[14px] font-bold text-danger disabled:opacity-50"
              >
                탈퇴하기
              </button>
            </div>
          </>
        )}
      </Sheet>
    </>
  );
}
